import { api } from '../../lib/api-client';

export interface BridgeStatus {
  name: string;
  healthy: boolean;
  lastHeartbeatAt: string | null;
  lastUnitsAt: string | null;
  lastArInvoicesAt: string | null;
  status: Record<string, unknown> | null;
}

/** Where a document has got to on its way into AutoCount's books. */
export type PostingState = 'PENDING' | 'HANDED_OVER' | 'CONFIRMED_POSTED' | 'VARIANCE';

export interface DocumentRow {
  uid: string;
  docType: string;
  docNo: string;
  docDate: string;
  /** A STRING. Money never becomes a JS number (CLAUDE.md §7.3). */
  totalAmount: string;
  debtorCode: string | null;
  state: PostingState;
  batchNo: string | null;
  autocountDocNo: string | null;
  varianceReason: string | null;
}

export interface BatchRow {
  uid: string;
  batchNo: string;
  periodStart: string;
  periodEnd: string;
  releasedAt: string | null;
  releasedBy: string | null;
  documentCount: number;
  totalAmount: string;
  confirmed: number;
  variance: number;
}

export interface Excluded {
  docNo: string;
  reason: string;
}

export interface ImportInstructions {
  dateFormat: string;
  duplicateAction: string;
  menuPath: string;
}

export interface KeyByHandLine {
  /** Blank on a labour line, which has no item code. */
  skuCode: string;
  description: string;
  qty: string;
  unitPrice: string;
  /** Already taken off `lineTotal`. */
  discountAmount: string;
  lineTotal: string;
}

export type KeyByHandDocType = 'INVOICE' | 'RECEIPT' | 'CREDIT_NOTE' | 'REFUND';

export interface KeyByHandPromotion {
  line: string;
  /** A STRING. */
  amount: string;
}

export interface KeyByHandDocument {
  docType: KeyByHandDocType;
  docNo: string;
  docDate: string;
  debtorCode: string;
  totalAmount: string;
  lines: KeyByHandLine[];
  /** A receipt's own wording; blank on the other types. */
  description: string | null;
  /** How a receipt was paid. */
  method: string | null;
  reference: string | null;
  /** The invoice number a receipt settles, once it is billed. */
  settles: string | null;
  /** On an invoice: the receipt numbers to knock off against it. */
  knockOff: string[];
  /** On an invoice: promotions to key as a credit note. */
  promotions: KeyByHandPromotion[];
}

export interface Preview {
  /** Pasted AND key-by-hand documents. */
  documentCount: number;
  /** Pasted plus key-by-hand; `keyByHandTotal` is the keyed share of it. */
  totalAmount: string;
  excluded: Excluded[];
  keyByHand: KeyByHandDocument[];
  /** The pasted share of `totalAmount`. */
  pastedTotal: string;
  keyByHandTotal: string;
  receiptsTotal: string;
  creditNotesTotal: string;
  refundsTotal: string;
  instructions: ImportInstructions;
}

export interface ReleasedBatch extends Preview {
  uid: string;
  batchNo: string;
  /** Tab separated, ready for the paste AutoCount expects. */
  clipboardText: string;
}

export const fetchBridgeStatus = (): Promise<BridgeStatus[]> =>
  api<BridgeStatus[]>('/accounting/bridge');

export const fetchDocuments = (): Promise<DocumentRow[]> =>
  api<DocumentRow[]>('/accounting/documents');

export const fetchBatches = (): Promise<BatchRow[]> =>
  api<BatchRow[]>('/accounting/handover/batches');

export const previewHandover = (from: string, to: string): Promise<Preview> =>
  api<Preview>(`/accounting/handover/preview?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`);

export const releaseHandover = (from: string, to: string): Promise<ReleasedBatch> =>
  api<ReleasedBatch>('/accounting/handover/batches', { method: 'POST', body: { from, to } });

export const fetchBatch = (uid: string): Promise<ReleasedBatch> =>
  api<ReleasedBatch>(`/accounting/handover/batches/${uid}`);

// ── matching our customers to AutoCount's debtors ────────────────────────────

export interface DebtorMatch {
  debtorCode: string;
  companyName: string;
  /** The last four characters only. */
  registerNoLast4: string | null;
  /** Another customer already claims this debtor. One each, both ways. */
  taken: boolean;
}

export interface UnmatchedCustomer {
  uid: string;
  name: string;
  documents: number;
}

export const searchDebtors = (q: string): Promise<DebtorMatch[]> =>
  api<DebtorMatch[]>(`/accounting/debtors/search?q=${encodeURIComponent(q)}`);

export interface Unmatched {
  customers: UnmatchedCustomer[];
  /** Unnamed counter sales waiting for the cash-sale debtor (Q87). */
  cashSales: number;
}

export const fetchUnmatchedCustomers = (): Promise<Unmatched> =>
  api<Unmatched>('/accounting/debtors/unmatched');

export const fetchCashSaleDebtor = (): Promise<{ debtorCode: string | null }> =>
  api<{ debtorCode: string | null }>('/accounting/debtors/cash-sale');

export const setCashSaleDebtor = (debtorCode: string): Promise<undefined> =>
  api<undefined>('/accounting/debtors/cash-sale', { method: 'PUT', body: { debtorCode } });

export const mapDebtor = (customerUid: string, debtorCode: string): Promise<undefined> =>
  api<undefined>('/accounting/debtors/map', {
    method: 'POST',
    body: { customerUid, debtorCode },
  });

// ── the AutoCount item codes a bike invoice is keyed under ───────────────────

export type FeeType = 'INSURANCE' | 'ROAD_TAX' | 'REGISTRATION' | 'SERVICE' | 'OTHER';

export interface FeeCode {
  feeType: FeeType;
  itemCode: string | null;
}

export const fetchFeeCodes = (): Promise<FeeCode[]> => api<FeeCode[]>('/accounting/fee-codes');

export const setFeeCode = (feeType: FeeType, itemCode: string): Promise<undefined> =>
  api<undefined>(`/accounting/fee-codes/${feeType}`, { method: 'PUT', body: { itemCode } });

export interface FinanceCompany {
  uid: string;
  name: string;
  autocountItemCode: string | null;
}

export const fetchFinanceCompanies = (): Promise<FinanceCompany[]> =>
  api<FinanceCompany[]>('/financing/providers');

export const setFinanceCompanyCode = (uid: string, itemCode: string): Promise<FinanceCompany> =>
  api<FinanceCompany>(`/financing/providers/${uid}/autocount-code`, { method: 'PUT', body: { itemCode } });
